import { confirmToast } from "../../utils/toast";
import React, { useEffect, useState } from 'react';
import axios from "../../utils/axiosConfig";

const ItemMaster = () => {
  const initialForm = {
    ItemCode: '',
    Commodity: '',
    ItemName: '',
    Form: '',
    ItemDescription: '',
    ISRI_ID: '',
    HSN_ID: ''
  };

  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(initialForm);
  const [items, setItems] = useState([]);
  const [isris, setIsris] = useState([]);
  const [hsns, setHsns] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  useEffect(() => {
    fetchItems();
    fetchISRI();
    fetchHSN();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await axios.get("/items");
      setItems(res.data);
    } catch (err) {
      alert("Failed to load items");
    }
  };

  const fetchISRI = async () => {
    try {
      const res = await axios.get(`${"/items"}/isri`);
      setIsris(res.data);
    } catch (err) {
      alert("Failed to load ISRI list");
    }
  };

  const fetchHSN = async () => {
    try {
      const res = await axios.get("/items/hsn");
      setHsns(res.data);
    } catch (err) {
      alert("Failed to load HSN list");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${"/items"}/${editingId}`, form);
      } else {
        await axios.post("/items", form);
      }

      alert(editingId ? "Item updated successfully" : "Item saved successfully");
      resetForm();
      fetchItems();
      setActiveTab("list");
    } catch (err) {
      alert("Save failed: " + (err.response?.data || err.message));
    }
  };

  const handleEdit = (item) => {
    setForm({
      ItemCode: item.ItemCode || '',
      Commodity: item.Commodity || '',
      ItemName: item.ItemName || '',
      Form: item.Form || '',
      ItemDescription: item.ItemDescription || '',
      ISRI_ID: item.ISRI_ID || '',
      HSN_ID: item.HSN_ID || ''
    });
    setEditingId(item.ItemID);
    setActiveTab("form");
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this item?")) return;

    try {
      await axios.delete(`${"/items"}/${id}`);
      fetchItems();
      if (editingId === id) resetForm();
    } catch (err) {
      alert("Delete failed");
    }
  };

  // Search filter
  const filteredItems = items.filter((row) => {
    const t = search.toLowerCase();
    if (!t) return true;
    return (
      (row.ItemName || '').toLowerCase().includes(t) ||
      (row.Form || '').toLowerCase().includes(t) ||
      (row.ISRICode || '').toLowerCase().includes(t) ||
      (row.HSNCode || '').toLowerCase().includes(t) ||
      (row.Commodity || '').toLowerCase().includes(t)
    );
  });

  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = filteredItems.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredItems.length / rowsPerPage);

  return (
    <div className="container mt-3">

      <div className="mb-3">
        <h4 className="fw-bold">📦 Item Master</h4>
      </div>

      <div className="card shadow-sm border-0">

        {/* Tabs */}
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                Item List
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => {
                  resetForm();
                  setActiveTab("form");
                }}
              >
                {editingId ? "Edit Item" : "Add Item"}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">

          {/* ================= LIST TAB ================= */}
          {activeTab === "list" && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Item Name</th>
                      <th>Form</th>
                      <th>ISRI</th>
                      <th>HSN</th>
                      <th>Commodity</th>
                      <th>Description</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-3">
                          No items found.
                        </td>
                      </tr>
                    ) : (
                      currentData.map((row) => (
                        <tr key={row.ItemID}>
                          <td>{row.ItemID}</td>
                          <td>{row.ItemName}</td>
                          <td>{row.Form}</td>
                          <td>{row.ISRICode || '-'}</td>
                          <td>{row.HSNCode || '-'}</td>
                          <td>{row.Commodity}</td>
                          <td>{row.ItemDescription}</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(row)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(row.ItemID)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirst + 1} to{" "}
                    {Math.min(indexOfLast, filteredItems.length)} of{" "}
                    {filteredItems.length} entries
                  </div>

                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index}
                          className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}

          {/* ================= FORM TAB ================= */}
          {activeTab === "form" && (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Commodity</label>
                  <input
                    name="Commodity"
                    value={form.Commodity}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Form *</label>
                  <input
                    name="Form"
                    value={form.Form}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Item Code *</label>
                  <input
                    name="ItemCode"
                    value={form.ItemCode}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Item Name *</label>
                  <input
                    name="ItemName"
                    value={form.ItemName}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">ISRI Code</label>
                  <select
                    name="ISRI_ID"
                    value={form.ISRI_ID}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">-- Select ISRI --</option>
                    {isris.map(i => (
                      <option key={i.Id} value={i.Id}>
                        {i.ISRICode}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">HSN Code</label>
                  <select
                    name="HSN_ID"
                    value={form.HSN_ID}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">-- Select HSN --</option>
                    {hsns.map(h => (
                      <option key={h.HSN_ID} value={h.HSN_ID}>
                        {h.HSNCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Description</label>
                  <input
                    name="ItemDescription"
                    value={form.ItemDescription}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

              </div>

              <div className="mt-4 text-end">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveTab("list")}
                >
                  Back to List
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default ItemMaster;
